import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { ExpressDocType } from "../types/express-document.type";
import { featureAccess } from "../permissions/access";
import { useUserRole } from "../utils/hooks/useUserRole";
import { useDebounce } from "../utils/hooks/useDebounce";
import { useBranches } from "../components/SpecialOrder/useBranches";
import ListFilter from "../components/ExpressDocument/ListFilter";
import DocumentTable from "../components/ExpressDocument/DocumentTable";
import {
  DocumentListFilterValue,
  EMPTY_DOCUMENT_LIST_FILTER,
  documentFilterParams,
} from "../components/ExpressDocument/documentListFilter";
import {
  EXPRESS_DOC_TYPE_CONFIG,
  documentNewPath,
  documentPageTitle,
} from "../components/ExpressDocument/constants";

const SEARCH_DEBOUNCE_MS = 500;

/** The list of one Express document type: ซื้อเชื่อ, ซื้อสด, รายได้อื่นๆ or รับชำระหนี้. */
export default function ExpressDocumentListPage({ docType }: Readonly<{ docType: ExpressDocType }>) {
  const config = EXPRESS_DOC_TYPE_CONFIG[docType];
  const navigate = useNavigate();
  const role = useUserRole();
  const canSelectBranch = featureAccess.expressDocumentAllBranches(role);
  const branches = useBranches(canSelectBranch);

  const [filter, setFilter] = useState<DocumentListFilterValue>(EMPTY_DOCUMENT_LIST_FILTER);
  const debouncedSearch = useDebounce(filter.search, SEARCH_DEBOUNCE_MS);
  const { branchCode, dateFrom, dateTo, status } = filter;
  // Rebuilt only when a filter (or the settled search) changes, so typing does not refetch.
  const filterParams = useMemo(
    () =>
      documentFilterParams(docType, {
        branchCode,
        dateFrom,
        dateTo,
        status,
        search: debouncedSearch,
      }),
    [docType, branchCode, dateFrom, dateTo, status, debouncedSearch],
  );

  const handleFilterChange = (patch: Partial<DocumentListFilterValue>) =>
    setFilter((previous) => ({ ...previous, ...patch }));
  const handleCreate = () => navigate(documentNewPath(config));

  return (
    <Stack sx={{ padding: "8px 0" }} gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Typography variant="h6" fontWeight={700}>
          {documentPageTitle(config)}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          บันทึกเอกสารใหม่
        </Button>
      </Stack>
      <Paper sx={{ padding: "24px 16px" }}>
        <ListFilter
          canSelectBranch={canSelectBranch}
          branches={branches}
          value={filter}
          onChange={handleFilterChange}
        />
      </Paper>
      <DocumentTable config={config} params={filterParams} />
    </Stack>
  );
}
